import { useQueryWithSSRErrors } from "@/shared/hooks";
import { ProductPurchasesQuery } from "../graphql";
import type {
  ProductPurchasesQueryType,
  ProductPurchasesQueryVariablesType,
} from "@/types/graphql/site.generated";

const LIMIT = 12;

/**
 * `skip` нужен гостю: страница покупок показывает ему гейт входа, а оба
 * запроса требуют авторизации и до гейта уходили на сервер впустую.
 */
export function useProductPurchases({ skip }: { skip?: boolean } = {}) {
  const variables: ProductPurchasesQueryVariablesType = {
    page: 0,
    rowsPerPage: LIMIT,
  };

  const { data, loading, error, fetchMore, called } = useQueryWithSSRErrors<
    ProductPurchasesQueryType,
    ProductPurchasesQueryVariablesType
  >(ProductPurchasesQuery, {
    variables,
    skip,
    notifyOnNetworkStatusChange: true,
  });

  const items = data?.productPurchases?.nodes ?? [];
  const count = data?.productPurchases?.count ?? 0;
  const hasMore = items.length < count;

  const loadMore = () => {
    if (loading || !hasMore) return;
    const nextPage = Math.floor(items.length / LIMIT);
    void fetchMore({ variables: { ...variables, page: nextPage } });
  };

  // Apollo's `called` is true from the very first render for a non-skipped
  // useQuery, so it can't tell "first fetch still in flight" apart from
  // "already resolved" — use data presence instead (undefined until the
  // first response lands, same idiom as useProduct's `product`).
  const hasLoaded = data !== undefined;

  return { items, count, loading, error, hasMore, loadMore, called, hasLoaded };
}
